// When the tool is embedded in an <iframe> on the WordPress site, report our
// true content height to the parent page so it can size the iframe to fit (no
// awkward inner scrollbar). No-op when not embedded (standalone site untouched).
//
// Why this is subtle:
//   The parent controls the iframe's *viewport* height. Anything measured
//   against the viewport (documentElement.scrollHeight, or a body/#root that
//   min-height:100% has stretched to fill the frame) is "pinned": it can grow
//   but never reports a value smaller than the frame the parent last set. That
//   makes the height ratchet UP only — navigate from a tall step back to a short
//   one and the frame stays tall with a dead empty band at the bottom.
//
//   Fix, in two halves:
//   (1) Neutralize the ONE viewport-fill that pins us — html/body/#root
//       height:100% and the app shell's min-h-full — so content is always its
//       own natural height in every engine (Chrome, and critically Safari/iOS/
//       Firefox where percentage min-height resolves against the definite iframe
//       viewport). We target a stable data-attribute on the shell root, NOT a
//       broad utility-class sweep, so we never collapse an unrelated .h-full
//       (e.g. the ExtractingStep reactor rings) to zero while embedded.
//   (2) Measure a natural-height CONTENT box (#root.scrollHeight) — a number
//       that both grows AND shrinks with real content. We deliberately never
//       read documentElement.scrollHeight, the one quantity that is
//       viewport-pinned and can only ratchet up.

const MESSAGE_TYPE = 'wbwh-estimate'
const STYLE_MARKER = 'data-wbwh-embed'

// If the parent never acknowledges our height messages (its listener <script>
// was stripped by a WordPress security/"safe HTML"/cache plugin), we can't grow
// the frame — so after this long with no ack we enable our OWN inner scroll so
// tall steps (≈1386px) don't get clipped-and-unreachable under scrolling="no".
const ACK_GRACE_MS = 2500

export function initIframeAutoResize() {
  // Standalone site and the /ops dashboard are never embedded — bail entirely
  // so no neutralization CSS or observers touch them.
  if (typeof window === 'undefined' || window.parent === window) return

  // Idempotency: a second call (dev HMR, a future remount) must not stack
  // duplicate stylesheets / observers / listeners.
  if (document.querySelector('style[' + STYLE_MARKER + ']')) return

  // ---------------------------------------------------------------------------
  // 1) Neutralize the viewport-fill so content is natural height in EVERY
  //    browser — narrowly.
  //
  // index.css pins html,body,#root to height:100%, and the app shell root is
  // <div data-embed-root class="flex min-h-full flex-col"> (min-height:100%).
  // Inside an iframe the percentage base is the parent-controlled (definite)
  // viewport, so those rules stretch the app to the frame height and defeat
  // shrink detection.
  //
  // We collapse ONLY those exact pins: the height:100% chain, and the shell root
  // via its data-embed-root hook. We do NOT sweep .h-full / .h-screen utility
  // classes — ExtractingStep ships `absolute inset-0 h-full w-full` reactor
  // rings and a data-plate <img>, and any future non-inset .h-full would
  // silently collapse to zero height ONLY while embedded (invisible in dev).
  // Targeting the shell's own attribute keeps the fix surgical and legible to
  // anyone editing a step component.
  // ---------------------------------------------------------------------------
  const style = document.createElement('style')
  style.setAttribute(STYLE_MARKER, '')
  style.textContent = [
    'html,body,#root{height:auto!important;min-height:0!important}',
    // The app shell's fill root — the only min-h-full in the estimate flow.
    // Collapsing its min-height lets #root shrink to content on shrink-nav.
    '#root [data-embed-root]{min-height:0!important;height:auto!important}',
    // NOTE: the fixed radial-gradient background (background-attachment:fixed in
    // index.css) is left untouched. It paints against the viewport but adds
    // nothing to layout height, so it neither pins the box nor feeds back into
    // the reported height. prefers-reduced-motion (also in index.css) is
    // untouched — this stylesheet is layout-only and adds no animation.
  ].join('')
  document.head.appendChild(style)

  const root = document.getElementById('root') || document.body

  // ---------------------------------------------------------------------------
  // 2) Measure the true CONTENT height — a box that grows AND shrinks.
  //
  // Primary term: #root.scrollHeight. Once the pins above are neutralized, #root
  // is a natural-height shrink-to-fit box, so scrollHeight rises when a taller
  // step mounts and FALLS when the user navigates back to a shorter step — it
  // has no relationship to the parent-controlled iframe viewport. body.scrollHeight
  // is a second content-box floor that also includes body's safe-area padding.
  //
  // We deliberately NEVER read document.documentElement.scrollHeight: the spec
  // clamps it to at least the (parent-controlled) viewport height, so it can
  // only ratchet up and was the mechanical cause of the stuck-tall bug. Do not
  // reintroduce it into this max() as a "defensive" floor.
  // ---------------------------------------------------------------------------
  const measure = () => {
    const body = document.body
    return Math.ceil(
      Math.max(
        root.scrollHeight,
        body ? body.scrollHeight : 0,
        // getBoundingClientRect().bottom in document space is an independent
        // cross-check that catches anything overflowing #root's own border box
        // (an absolutely-positioned toast, a child taller than #root in some
        // engine). Also a content box — it shrinks too — so it can't ratchet.
        root.getBoundingClientRect().bottom + (window.scrollY || 0),
      ),
    )
  }

  // ---------------------------------------------------------------------------
  // 3) Post — deduped, coalesced, and settle-corrected.
  //
  // - Dedup by last-sent height so idle observer fires never spam the parent.
  // - Coalesce bursts (a React commit fires MutationObserver/ResizeObserver in
  //   tight clusters; the 'extracting' step swaps a fact-card every ~3.8s) with
  //   a short setTimeout microbatch.
  // - A trailing settle re-measure re-corrects DOWN if a burst was captured
  //   mid-fade/animation (a transient overshoot), so a one-time large value can
  //   never lock in even after observers go quiet.
  //
  // We deliberately do NOT use requestAnimationFrame for the recurring debounce:
  // rAF is throttled/paused when the iframe is offscreen (background tab / below
  // the fold), which would stall the very resize we need. setTimeout keeps
  // firing regardless of visibility. (A single rAF is used ONCE below, only for
  // the first post-paint measure, where visibility isn't yet a concern — this
  // avoids a first-frame flash from a pre-layout measurement.)
  // ---------------------------------------------------------------------------
  let last = 0
  let acked = false
  let coalesceTimer = 0
  let settleTimer = 0

  const send = () => {
    coalesceTimer = 0
    const height = measure()
    // Guard against a transient 0/near-0 during teardown/reflow (a real short
    // step is still hundreds of px) so we can't post a spurious collapse.
    if (height > 0 && height !== last) {
      last = height
      window.parent.postMessage({ type: MESSAGE_TYPE, height }, '*')
    }
  }

  const post = () => {
    // Always (re)arm the trailing settle re-measure. It's cheap — one measure +
    // a dedup check, nothing posted unless the height actually changed.
    clearTimeout(settleTimer)
    settleTimer = window.setTimeout(send, 250)
    if (coalesceTimer) return
    // ~4 frames of coalescing without depending on rAF visibility: long enough
    // to fold one React commit's burst (and the extracting fact-card swap) into
    // a single message, short enough to feel instant.
    coalesceTimer = window.setTimeout(send, 64)
  }

  // ---------------------------------------------------------------------------
  // 4) Triggers.
  // ---------------------------------------------------------------------------

  // Size changes: observe #root and <body> directly. Both are natural-height
  // now, so a ResizeObserver fires on SHRINK as well as grow — that shrink fire
  // is exactly what unsticks us from a tall step. We do NOT observe
  // documentElement (viewport-pinned; would fire on frame-driven resizes and
  // could form a feedback loop). Because measure() reads #root's live box, the
  // observer never needs re-pointing when React swaps the subtree.
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(post)
    ro.observe(root)
    if (document.body && document.body !== root) ro.observe(document.body)
  }

  // Content changes: step swaps and the extracting fact-card swap. childList +
  // subtree only — NOT attributes — so the continuous transform/opacity
  // keyframes on the reactor don't fire this every frame.
  if ('MutationObserver' in window) {
    const mo = new MutationObserver(post)
    mo.observe(root, { childList: true, subtree: true })
  }

  // Re-show inside a WordPress tab / accordion: when an ANCESTOR on the parent
  // page toggles display, the iframe sees no DOM/size change of its own and
  // would otherwise stay at a stale height. IntersectionObserver fires when we
  // re-enter the viewport, and visibilitychange covers a background→foreground
  // tab flip. Both simply re-measure.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) post()
    })
    io.observe(root)
  }
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) post()
  })

  // Web fonts (Inter/Sora) load async and reflow text — re-measure on ready
  // plus a defensive follow-up.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      post()
      setTimeout(post, 60)
    })
  }

  // Late <img> decode reflow (data-plate photo) — capture phase catches load
  // events that don't bubble.
  window.addEventListener('load', post)
  window.addEventListener('resize', post)
  document.addEventListener('load', post, true)

  // No-listener fallback: if the parent never acknowledges (its <script> was
  // stripped), enable our own inner scroll so tall steps aren't clipped and
  // unreachable under scrolling="no". The parent pings back a lightweight ack.
  window.addEventListener('message', (event) => {
    const d = event.data
    if (d && d.type === MESSAGE_TYPE + '-ack') acked = true
  })
  setTimeout(() => {
    if (!acked) {
      // Frame is stuck at its inline min-height and can't grow — restore normal
      // document scrolling so all content stays reachable. (Harmless if the
      // parent's listener exists but simply doesn't send acks: we only reach
      // here when zero acks arrived, and the height messages keep working.)
      const s = document.createElement('style')
      s.setAttribute(STYLE_MARKER + '-fallback', '')
      s.textContent = 'html,body{height:auto!important;overflow:auto!important}'
      document.head.appendChild(s)
    }
  }, ACK_GRACE_MS)

  // Initial passes. Gate the FIRST measure behind a single rAF (safe at load —
  // visibility isn't a concern yet) so we measure after first paint/layout, not
  // a pre-font pre-layout height, avoiding a first-frame flash. Then settle
  // passes cover the font swap and layout settling.
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      requestAnimationFrame(send)
    })
  } else {
    send()
  }
  setTimeout(post, 120)
  setTimeout(post, 400)
  setTimeout(post, 1200)
}
